// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Dataset definition service — agent-owned dataset CRUD + listing.
 *
 * Backed by /org/{orgId}/ws/{wsId}/dataset-definitions (distinct from
 * /datasets, which serves analysis outputs). Mutations are global-admin only,
 * except the user-sections update which is open to workspace admins.
 */
import type { NodeApiClient } from '@/app-shell/services'
import type { DatasetRecord } from '../types'

export interface DatasetUpsertRequest {
  label: string
  description?: string | null
  schemaId?: string | null
  knowledgeBase?: string | null
  systemPrompt?: string | null
  userSections?: Record<string, string> | null
  knowledgeBaseBatchSize?: number | null
  pageMode?: boolean
  diffMode?: boolean
}

/** List every agent dataset in a workspace (each tagged with its agentId). */
export async function listDatasets(
  apiClient: NodeApiClient,
  orgId: string,
  wsId: string,
): Promise<DatasetRecord[]> {
  const url = `/org/${orgId}/ws/${wsId}/dataset-definitions`
  const res = await apiClient.get<DatasetRecord[]>(url)
  return res.data
}

/** List the datasets owned by a single agent. */
export async function listAgentDatasets(
  apiClient: NodeApiClient,
  orgId: string,
  wsId: string,
  agentId: string,
): Promise<DatasetRecord[]> {
  const url = `/org/${orgId}/ws/${wsId}/dataset-definitions/${agentId}`
  const res = await apiClient.get<DatasetRecord[]>(url)
  return res.data
}

/** Create or update a dataset owned by an agent. */
export async function upsertDataset(
  apiClient: NodeApiClient,
  orgId: string,
  wsId: string,
  agentId: string,
  datasetKey: string,
  request: DatasetUpsertRequest,
): Promise<DatasetRecord> {
  const url = `/org/${orgId}/ws/${wsId}/dataset-definitions/${agentId}/${datasetKey}`
  const res = await apiClient.put<DatasetRecord>(url, request)
  return res.data
}

/** Update only the user-editable section values of a dataset (workspace admins). */
export async function updateDatasetUserSections(
  apiClient: NodeApiClient,
  orgId: string,
  wsId: string,
  agentId: string,
  datasetKey: string,
  userSections: Record<string, string>,
): Promise<DatasetRecord> {
  const url = `/org/${orgId}/ws/${wsId}/dataset-definitions/${agentId}/${datasetKey}/user-sections`
  const res = await apiClient.put<DatasetRecord>(url, { userSections })
  return res.data
}

/** Delete a dataset owned by an agent. */
export async function deleteDataset(
  apiClient: NodeApiClient,
  orgId: string,
  wsId: string,
  agentId: string,
  datasetKey: string,
): Promise<void> {
  const url = `/org/${orgId}/ws/${wsId}/dataset-definitions/${agentId}/${datasetKey}`
  await apiClient.delete(url)
}

/** Clone a dataset into another agent (same workspace) under a new key. */
export async function cloneDataset(
  apiClient: NodeApiClient,
  orgId: string,
  wsId: string,
  agentId: string,
  datasetKey: string,
  targetAgentId: string,
  newKey: string,
): Promise<DatasetRecord> {
  const url = `/org/${orgId}/ws/${wsId}/dataset-definitions/${agentId}/${datasetKey}/clone`
  const res = await apiClient.post<DatasetRecord>(url, { targetAgentId, newKey })
  return res.data
}
